import { Button } from "@/components/ui/button"
import { Lock, Sparkles, ArrowRight } from "lucide-react"
import { getFeatureDisplayName, FeatureKey } from '@/app/config/features'
import { useUpgradeModal } from '@/app/contexts/UpgradeModalContext'
import { useAppSelector, selectUserOrganisation } from '@app.raytd.com/store'
import { isRoleGreaterThanOrEqual } from '@/app/utils/roles'

interface FeatureGateProps {
    feature: FeatureKey
    embedded?: boolean // If true, renders without full-page height (for inside modals/panels)
}

export const FeatureGate = ({ feature, embedded = false }: FeatureGateProps) => {
    const { triggerUpgrade } = useUpgradeModal()
    const organisation = useAppSelector(selectUserOrganisation)
    const featureName = getFeatureDisplayName(feature)
    
    // Check if user is admin
    const isAdmin = organisation && isRoleGreaterThanOrEqual(
        (organisation.role || 'guest') as 'admin' | 'member' | 'guest',
        'admin'
    )

    const containerClass = embedded 
        ? "w-full h-full flex items-center justify-center p-4 bg-zinc-50/50 rounded-lg border-2 border-dashed border-zinc-200"
        : "w-full h-[calc(100vh-4rem)] flex items-center justify-center bg-zinc-50 p-4"

    return (
        <div className={containerClass}>
            <div className="max-w-md w-full text-center space-y-6">
                <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm">
                    <Lock className="w-8 h-8 text-blue-600" />
                </div>

                <div className="space-y-2">
                    <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">
                        Unlock {featureName}
                    </h2>
                    <p className="text-zinc-500 text-sm leading-relaxed">
                        This feature is part of our <strong>Pro Plan</strong>. 
                        Upgrade your workspace to access advanced reporting, unlimited assets, and more.
                    </p>
                </div>

                <div className="pt-2">
                    {isAdmin ? (
                        <Button 
                            size="lg" 
                            className="w-full gap-2 shadow-blue-900/20 shadow-lg" 
                            onClick={() => triggerUpgrade(featureName)}
                        >
                            <Sparkles className="w-4 h-4" />
                            Upgrade to Pro
                        </Button>
                    ) : (
                        <div className="space-y-3">
                            <Button disabled variant="outline" className="w-full opacity-50 cursor-not-allowed">
                                Upgrade to Pro
                            </Button>
                            <p className="text-xs text-zinc-400">
                                Contact your workspace administrator to request access.
                            </p>
                        </div>
                    )}
                </div>

                {/* Social Proof / Mini Feature List could go here */}
                <div className="pt-6 border-t border-zinc-100">
                    <div className="flex items-center justify-center gap-2 text-xs font-medium text-zinc-600 cursor-pointer hover:text-blue-600 transition-colors" onClick={() => window.open('https://raytd.com/pricing', '_blank')}>
                        Compare plans <ArrowRight className="w-3 h-3" />
                    </div>
                </div>
            </div>
        </div>
    )
}

